import { Schema } from '@coveo/bueno';
import { configurationReducer as configuration } from '../../../features/configuration/configuration-slice.js';
import { foldingOptionsSchemaDefinition, registerFolding, } from '../../../features/folding/folding-actions.js';
import { foldingReducer as folding } from '../../../features/folding/folding-slice.js';
import { queryReducer as query } from '../../../features/query/query-slice.js';
import { searchReducer as search } from '../../../features/search/search-slice.js';
import { loadReducerError } from '../../../utils/errors.js';
import { validateOptions } from '../../../utils/validate-payload.js';
import { buildCoreResultList, } from '../result-list/headless-core-result-list.js';
const optionsSchema = new Schema(foldingOptionsSchemaDefinition);
/**
 * Creates a core `FoldedResultList` controller instance.
 *
 * @param engine - The headless engine.
 * @param props - The configurable `FoldedResultList` properties.
 * @param analyticsClient - A FoldedResultAnalyticsClient to send the appropriate analytics calls.
 * @returns A `FoldedResultList` controller instance.
 *
 * @group Controllers
 * @category FoldedResultList
 */
export function buildCoreFoldedResultList(engine, props, analyticsClient) {
    if (!loadFoldingReducer(engine)) {
        throw loadReducerError;
    }
    const controller = buildCoreResultList(engine, props);
    const { dispatch } = engine;
    const getState = () => engine.state;
    const options = props.options?.folding
        ? validateOptions(engine, optionsSchema, props.options.folding, 'buildFoldedResultList')
        : {};
    dispatch(registerFolding({ ...options }));
    return {
        ...controller,
        loadCollection: (collection) => {
            dispatch(props.loadCollectionActionCreator(collection.result.raw[engine.state.folding.fields.collection]));
            dispatch(analyticsClient.logShowMoreFoldedResults(collection.result));
        },
        logShowMoreFoldedResults: (result) => {
            dispatch(analyticsClient.logShowMoreFoldedResults(result));
        },
        logShowLessFoldedResults: () => {
            dispatch(analyticsClient.logShowLessFoldedResults());
        },
        findResultById(collection) {
            return searchForResult(this.state.results, (r) => r.result.uniqueId === collection.result.uniqueId);
        },
        findResultByCollection(collection) {
            return searchForResult(this.state.results, (r) => r.result.raw.foldingcollection ===
                collection.result.raw.foldingcollection);
        },
        get state() {
            const state = getState();
            return {
                ...controller.state,
                results: controller.state.results.map((result) => {
                    const collectionId = result.raw[state.folding.fields.collection];
                    if (!collectionId || !state.folding.collections[collectionId]) {
                        return {
                            result,
                            moreResultsAvailable: false,
                            isLoadingMoreResults: false,
                            children: [],
                        };
                    }
                    return state.folding.collections[collectionId];
                }),
            };
        },
    };
}
function loadFoldingReducer(engine) {
    engine.addReducers({ search, configuration, folding, query });
    return true;
}
function searchForResult(results, compareCb) {
    for (let i = 0; i < results.length; i++) {
        const result = results[i];
        if (compareCb(result)) {
            return result;
        }
        if (result.children.length) {
            const childResult = searchForResult(result.children, compareCb);
            if (childResult) {
                return childResult;
            }
        }
    }
    return null;
}
